"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDateBoundaries = exports.expandToEndInterval = exports.expandToStartInterval = exports.expandTimeWindowToInterval = exports.sliceWindowByInterval = exports.computeMsDifferenceForWindow = exports.windowGreaterThanDelta = exports.decreaseTimeByDelta = exports.standardizeDate = exports.timeDeltaToSeconds = exports.noDelta = exports.alignmentForTimezone = exports.alignmentForUtc = exports.DEFAULT_START_OF_WEEK_ISO = exports.MINIMUM_DATE = void 0;
const ee_nestjs_interfaces_1 = require("ee-nestjs-interfaces");
const luxon_1 = require("luxon");
// If start isn't specified we can use a few months before the first charger was installed.
exports.MINIMUM_DATE = new Date(Date.UTC(2023, 5, 1));
/*
 * Just a mapping from aggregation intervals to the plural form this is used by luxon, and not
 * exposed publically because it's not used externally yet.
 */
const intervalPlural = new Map([
    [ee_nestjs_interfaces_1.AggregationInterval.Second, "seconds"],
    [ee_nestjs_interfaces_1.AggregationInterval.Minute, "minutes"],
    [ee_nestjs_interfaces_1.AggregationInterval.Hour, "hours"],
    [ee_nestjs_interfaces_1.AggregationInterval.Day, "days"],
    [ee_nestjs_interfaces_1.AggregationInterval.Week, "weeks"],
    [ee_nestjs_interfaces_1.AggregationInterval.Month, "months"],
    [ee_nestjs_interfaces_1.AggregationInterval.Quarter, "quarters"],
    [ee_nestjs_interfaces_1.AggregationInterval.Year, "years"],
]);
// The system default start of week. 7 = Sunday
exports.DEFAULT_START_OF_WEEK_ISO = 7;
// The system default start of year. 1 = January
const DEFAULT_START_OF_YEAR_ISO = 1;
const DEFAULT_IS_FISCAL_YEAR_OFFSET = false;
/**
 * Returns a default time alignment for utc.
 */
function alignmentForUtc() {
    return alignmentForTimezone("utc");
}
exports.alignmentForUtc = alignmentForUtc;
/**
 * Returns a default time alignment for a given timezone.
 *
 * @param timezoneName The name of the timezone to use.
 */
function alignmentForTimezone(timezoneName) {
    return {
        startOfWeekIso: exports.DEFAULT_START_OF_WEEK_ISO,
        startOfYearIso: DEFAULT_START_OF_YEAR_ISO,
        isFiscalYearOffset: DEFAULT_IS_FISCAL_YEAR_OFFSET,
        timezoneName: timezoneName,
    };
}
exports.alignmentForTimezone = alignmentForTimezone;
/**
 * Returns a delta that represents when no delta exists.
 */
function noDelta() {
    return {
        interval: ee_nestjs_interfaces_1.AggregationInterval.Hour,
        count: 0,
    };
}
exports.noDelta = noDelta;
/**
 * Converts a delta into seconds for the delta.
 *
 * @param delta The delta to convert into a delta of seconds.
 *
 * @returns A count of seconds that represents the same time delta.
 */
function timeDeltaToSeconds(delta) {
    switch (delta.interval) {
        case ee_nestjs_interfaces_1.AggregationInterval.Second:
            return delta.count;
        case ee_nestjs_interfaces_1.AggregationInterval.Minute:
            return delta.count * 60;
        case ee_nestjs_interfaces_1.AggregationInterval.Hour:
            return delta.count * 60 * 60;
        case ee_nestjs_interfaces_1.AggregationInterval.Day:
            return delta.count * 60 * 60 * 24;
        case ee_nestjs_interfaces_1.AggregationInterval.Week:
            return delta.count * 60 * 60 * 24 * 7;
        case ee_nestjs_interfaces_1.AggregationInterval.Month:
        case ee_nestjs_interfaces_1.AggregationInterval.Quarter:
        case ee_nestjs_interfaces_1.AggregationInterval.Year:
            throw new Error(`Can't convert from this interval "${delta.interval}" to seconds.`);
    }
    throw new Error(`Interval "${delta.interval}" not handled in timeDeltaToSeconds.`);
}
exports.timeDeltaToSeconds = timeDeltaToSeconds;
function toLuxonDateTime(date, alignment) {
    if (date instanceof Date) {
        return luxon_1.DateTime.fromJSDate(date, { zone: alignment.timezoneName });
    }
    else if (typeof date === "string") {
        return luxon_1.DateTime.fromISO(date, { zone: alignment.timezoneName });
    }
    else if (typeof date === "number") {
        return luxon_1.DateTime.fromMillis(date, { zone: alignment.timezoneName });
    }
    else {
        throw new Error("Unsupported date format: must be a Date or ISO string");
    }
}
/**
 * Converts a date to a standardized JavaScript Date object using the specified time alignment.
 *
 * @param date - The input date as a Date object, ISO string, or timestamp number.
 * @param alignment - The time alignment configuration containing the timezone name.
 * @returns A standardized JavaScript Date object in the specified timezone.
 * @throws {Error} If an unsupported date format is provided.
 * @description
 * - Converts input date to a Luxon DateTime with specified timezone
 * - Converts Luxon DateTime back to a JavaScript Date object
 * - Ensures consistent date representation across different input formats
 * - Preserves timezone information from the alignment configuration
 */
function standardizeDate(date, alignment) {
    return toLuxonDateTime(date, alignment).toJSDate();
}
exports.standardizeDate = standardizeDate;
/**
 * Converts the delta into something that can be used by luxon to perform a plus or minus.
 *
 * @param delta The delta to convert into a luxon object type.
 *
 * @returns An object where the key is the plural name of the interval and a count to represent the delta.
 */
function deltaToLuxonRepresentation(delta) {
    const pluralName = intervalPlural.get(delta.interval);
    if (pluralName == undefined) {
        throw new Error(`Interval '${delta.interval}' was not found in intervalPlural.`);
    }
    return { [pluralName]: delta.count };
}
/**
 * Decreases the given date by the delta. Makes sure to use the alignment as needed.
 *
 * @param date The date to compute the delta from.
 * @param delta The delta used to subtract from the date.
 * @param alignment The time alignment to use in the computation if needed.
 *
 * @returns A date that is the date minus the delta.
 */
function decreaseTimeByDelta(date, delta, alignment = alignmentForUtc()) {
    return toLuxonDateTime(date, alignment)
        .minus(deltaToLuxonRepresentation(delta))
        .toJSDate();
}
exports.decreaseTimeByDelta = decreaseTimeByDelta;
/**
 * Determines if the start of a time window is greater than a specified time delta from the window's end.
 *
 * @param window The time window to evaluate, with optional start and end dates.
 * @param delta The time delta to subtract from the window's end.
 * @param alignment The time alignment configuration to use (defaults to UTC).
 *
 * @returns {boolean} True if the window's start is chronologically before the end minus the delta,
 *                    false otherwise.
 *
 * @description
 * - Uses the window's end date (or current date if not specified)
 * - Uses the window's start date (or MINIMUM_DATE if not specified)
 * - Subtracts the specified delta from the end date
 * - Compares the start date against the delta-adjusted end date
 */
function windowGreaterThanDelta(window, delta, alignment = alignmentForUtc()) {
    const end = toLuxonDateTime(window.end ?? new Date(), alignment).toJSDate();
    const start = toLuxonDateTime(window.start ?? exports.MINIMUM_DATE, alignment).toJSDate();
    const deltaStart = decreaseTimeByDelta(end, delta, alignment);
    return start < deltaStart;
}
exports.windowGreaterThanDelta = windowGreaterThanDelta;
/**
 * Computes the number of milliseconds between the window using alignment timezone in case this is over changes in DST
 *
 * @param window The window to compute the difference in milliseconds from.
 * @param alignment The alignment to use for computing the ms difference.
 *
 * @returns The count of milliseconds between the two.
 */
function computeMsDifferenceForWindow(window, alignment = alignmentForUtc()) {
    const end = toLuxonDateTime(window.end ?? new Date(), alignment);
    const start = toLuxonDateTime(window.start ?? exports.MINIMUM_DATE, alignment);
    if (end < start) {
        throw new Error(`Something must have gone wrong as the start and end are backwards: ${start}, ${end}`);
    }
    return end.diff(start).as("milliseconds");
}
exports.computeMsDifferenceForWindow = computeMsDifferenceForWindow;
/**
 * Slices the entire window of time into interval chunks based on the interval specified. With the
 * ability to specify at the end of the time windows a buffer in case there's a period of time we
 * shouldn't cache near the end so we'll keep that interval together to allow for recalculating over
 * that period of time.
 *
 * @param window An optional window (represents all of time if no window is provided) which we will slice if it's possible to.
 * @param interval The interval to slice the window by.
 * @param alignment The alignment to use in order to adjust intervals by just in case.
 * @param lastWindowBuffer The buffer to use which should be considered volatile data that may need to be recalculated so
 *     don't make an interval that ends in that buffer before the last interval. If it's a zero delta then it acts as if doesn't exist.
 *
 * @returns An array of timewindows representing the sliced up windows.
 */
function sliceWindowByInterval(window, interval = ee_nestjs_interfaces_1.AggregationInterval.Day, alignment = alignmentForUtc(), lastWindowBuffer = noDelta()) {
    if ((window?.end ?? new Date()) < (window?.start ?? exports.MINIMUM_DATE)) {
        throw new Error(`Something must have gone wrong as the start and end are backwards: ${window?.start ?? exports.MINIMUM_DATE}, ${window?.end ?? new Date()}`);
    }
    const beforeLastWindowBuffer = luxon_1.DateTime.now()
        .setZone(alignment.timezoneName)
        .minus(deltaToLuxonRepresentation(lastWindowBuffer));
    const lastWindowStart = toLuxonDateTime(expandToStartInterval(beforeLastWindowBuffer.toJSDate(), interval, alignment), alignment);
    const start = toLuxonDateTime(window?.start ?? exports.MINIMUM_DATE, alignment);
    const end = toLuxonDateTime(window?.end ?? new Date(), alignment);
    if (start >= beforeLastWindowBuffer || start >= lastWindowStart) {
        return [
            {
                start: start.toJSDate(),
                end: end.toJSDate(),
            },
        ];
    }
    const intervals = [];
    let currentStartInterval = start;
    let currentEndInterval = toLuxonDateTime(expandToEndInterval(start.toJSDate(), interval, alignment), alignment);
    const minEnd = luxon_1.DateTime.min(end, beforeLastWindowBuffer);
    let intervalsEnd = minEnd.minus({ [interval]: 1 });
    intervalsEnd =
        intervalsEnd < currentStartInterval
            ? minEnd
            : toLuxonDateTime(expandToEndInterval(intervalsEnd.toJSDate(), interval, alignment), alignment);
    while (currentEndInterval <= intervalsEnd) {
        intervals.push({
            start: currentStartInterval.toJSDate(),
            end: currentEndInterval.toJSDate(),
        });
        currentStartInterval = toLuxonDateTime(expandToStartInterval(currentStartInterval.plus({ [interval]: 1 }).toJSDate(), interval, alignment), alignment);
        currentEndInterval = toLuxonDateTime(expandToEndInterval(currentStartInterval.toJSDate(), interval, alignment), alignment);
    }
    intervals.push({
        start: currentStartInterval.toJSDate(),
        end: end.toJSDate(),
    });
    return intervals;
}
exports.sliceWindowByInterval = sliceWindowByInterval;
/**
 * Expands the window to fit the boundary of the intervals. For example if it's a day interval then
 * it will cause the start interval to start at the beginning of the day, and similar for the end and the end of the day.
 *
 * @param window The window of time which will be expanded.
 * @param interval The interval to use when expanding.
 * @param alignment The alignment to use when performing these expansions.
 *
 * @returns A window representing the expanded window that contains the entire time frame.
 */
function expandTimeWindowToInterval(window, interval = ee_nestjs_interfaces_1.AggregationInterval.Day, alignment = alignmentForUtc()) {
    return {
        start: expandToStartInterval(window?.start, interval, alignment),
        end: expandToEndInterval(window?.end, interval, alignment),
    };
}
exports.expandTimeWindowToInterval = expandTimeWindowToInterval;
/**
 * Expands the date to the start of the given interval. For example if it's a day interval then
 * it will cause the date to match the start of that day.
 *
 * @param date The date that will be used to find the start of the interval.
 * @param interval The interval to use when expanding.
 * @param alignment The alignment to use when performing this expansion.
 *
 * @returns A date representing the start of the interval for the given date.
 */
function expandToStartInterval(date, interval = ee_nestjs_interfaces_1.AggregationInterval.Day, alignment = alignmentForUtc()) {
    const dateTime = date
        ? toLuxonDateTime(date, alignment)
        : luxon_1.DateTime.now().setZone(alignment.timezoneName);
    if (interval === ee_nestjs_interfaces_1.AggregationInterval.AllTime) {
        return exports.MINIMUM_DATE;
    }
    if (interval === ee_nestjs_interfaces_1.AggregationInterval.Year) {
        const yearAdjustment = dateTime.month < alignment.startOfYearIso ? -1 : 0;
        return dateTime
            .plus({ years: yearAdjustment })
            .startOf(ee_nestjs_interfaces_1.AggregationInterval.Year)
            .plus({ months: alignment.startOfYearIso - 1 })
            .toJSDate();
    }
    else if (interval === ee_nestjs_interfaces_1.AggregationInterval.Quarter) {
        // Add a year so that the modulo numerator is positive.
        // Months are zero based in moment and 1 based in python, hence the extra +1.
        const monthDiff = -((dateTime.month + 12 - alignment.startOfYearIso) % 3);
        return dateTime
            .startOf(ee_nestjs_interfaces_1.AggregationInterval.Month)
            .plus({ months: monthDiff })
            .toJSDate();
    }
    else {
        return dateTime.startOf(interval).toJSDate();
    }
}
exports.expandToStartInterval = expandToStartInterval;
/**
 * Expands the date to the end of the given interval. For example if it's a day interval then
 * it will cause the date to match the end of that day.
 *
 * @param date The date that will be used to find the end of the interval.
 * @param interval The interval to use when expanding.
 * @param alignment The alignment to use when performing this expansion.
 *
 * @returns A date representing the end of the interval for the given date.
 */
function expandToEndInterval(date, interval = ee_nestjs_interfaces_1.AggregationInterval.Day, alignment = alignmentForUtc()) {
    if (interval === ee_nestjs_interfaces_1.AggregationInterval.AllTime) {
        return luxon_1.DateTime.now().setZone(alignment.timezoneName).toJSDate();
    }
    const startInterval = expandToStartInterval(date, interval, alignment);
    if (interval === ee_nestjs_interfaces_1.AggregationInterval.Year) {
        return toLuxonDateTime(startInterval, alignment)
            .plus({ years: 1 })
            .minus({ days: 1 })
            .endOf(ee_nestjs_interfaces_1.AggregationInterval.Day)
            .toJSDate();
    }
    else if (interval === ee_nestjs_interfaces_1.AggregationInterval.Quarter) {
        return toLuxonDateTime(startInterval, alignment)
            .plus({ months: 3 })
            .minus({ days: 1 })
            .endOf(ee_nestjs_interfaces_1.AggregationInterval.Day)
            .toJSDate();
    }
    else {
        return toLuxonDateTime(startInterval, alignment).endOf(interval).toJSDate();
    }
}
exports.expandToEndInterval = expandToEndInterval;
/**
 * Convert startDay and endDay with timezone to Date objects representing day boundaries.
 * @param startDay YYYY-MM-DD format
 * @param endDay YYYY-MM-DD format
 * @param timezone IANA timezone string
 * @returns Object with start and end Date objects representing midnight in the given timezone
 */
function getDateBoundaries(startDay, endDay, timezone) {
    const alignment = alignmentForTimezone(timezone);
    // Use ee-utils to find the start of the day for startDay.
    const start = expandToStartInterval(startDay, ee_nestjs_interfaces_1.AggregationInterval.Day, alignment);
    // Use ee-utils to find the start of the day for endDay (which is the end boundary).
    const end = expandToStartInterval(endDay, ee_nestjs_interfaces_1.AggregationInterval.Day, alignment);
    return { start, end };
}
exports.getDateBoundaries = getDateBoundaries;
